import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { server } from "src/coreConfig";
import { setShopState } from "src/slices/shop";
import { useDispatch, useSelector } from "src/store";
import { handleDownloadFile } from "src/utils/dataRenders";
import useAsyncRequest from "./useAsyncRequest";

const shopUrls = {
  userAccount: {
    updateAvatar: `${server}/api/v2/shop/useraccount/updateAvatar/`,
    addFundOptions: `${server}/api/v2/shop/useraccount/addFundOptions/`,
    detail: `${server}/api/v2/shop/useraccount/detail/`,
    transactionDetail: `${server}/api/v2/shop/useraccount/transactionDetail/`,
    activateTransactions: `${server}/api/v2/shop/useraccount/activateTransactions/`,
    inactivateTransaction: `${server}/api/v2/shop/useraccount/inactivateTransaction/`,
    search: `${server}/api/v2/shop/useraccount/search/`,
    checkDocId: `${server}/api/v2/shop/useraccount/checkDocId/`,
    checkExternalServices: `${server}/api/v2/shop/useraccount/checkExternalServices/`,
  },
  deposit: {
    delete: `${server}/api/v2/shop/deleteDeposit/`,
    add: `${server}/api/v2/shop/deposit/`,
  },
  addFund: {
    main: `${server}/api/v2/shop/useraccount/addFund/`,
    list: `${server}/api/v2/shop/addFund/list/`,
    approveFromExcel: `${server}/api/v2/shop/addFund/approveFromExcel/`,
    detail: `${server}/api/v2/shop/addFund/detail/`,
    transfer: `${server}/api/v2/shop/addFund/transfer/`,
    transferDetail: `${server}/api/v2/shop/addFund/transferDetail/`,
    updateTransfer: `${server}/api/v2/shop/addFund/updateTransfer/`,
    stats: `${server}/api/v2/shop/addFund/stats/`,
    statsDownload: `${server}/api/v2/shop/addFund/report/Download/`,
    downloadVoucher: `${server}/api/v2/shop/useraccount/addFund/downloadVoucher/`,
    resendEmail: `${server}/api/v2/shop/useraccount/addFund/resendEmail/`,
    add: `${server}/api/v2/shop/useraccount/addFund/new/`,
    incomeReport: `${server}/api/v2/shop/addFund/incomeReport/`,
    incomeReportList: `${server}/api/v2/shop/addFund/incomeReportList/`,
    incomeReportDetail: `${server}/api/v2/shop/addFund/incomeReportDetail/`,
    incomeReportPdf: `${server}/api/v2/shop/addFund/incomeReportPdf/`,
  },
  reportPdf: {
    detail: `${server}/api/v2/shop/fundReportPdf/detail/`,
    list: `${server}/api/v2/shop/fundReportPdf/list/`,
    new: `${server}/api/v2/shop/fundReportPdf/new/`,
    update: `${server}/api/v2/shop/fundReportPdf/update/`,
    delete: `${server}/api/v2/shop/fundReportPdf/delete/`,
  },
  customerAccounts: `${server}/api/v2/shop/useraccount/customerAccounts/`,
  handle: `${server}/api/v2/shop/handle/`,
  index: `${server}/api/v2/shop/useraccount/`,
  customerList: `${server}/api/v2/shop/useraccount/customerList/`,
  paymentAccount: {
    bank_report_detail: `${server}/api/v2/shop/paymentAccount/bankFundReport/detail/`,
    bankFundReport: `${server}/api/v2/shop/paymentAccount/bankFundReport/`,
    delete_bank_report: `${server}/api/v2/shop/paymentAccount/bankFundReport/delete/`,
    loadCsv: `${server}/api/v2/shop/paymentAccount/load-csv/`,
  },
  paymentAgreement: {
    create: `${server}/api/v2/shop/paymentAgreement/create/`,
    update: `${server}/api/v2/shop/paymentAgreement/update/`,
    detail: `${server}/api/v2/shop/paymentAgreement/detail/`,
    list: `${server}/api/v2/shop/paymentAgreement/list/`,
    delete: `${server}/api/v2/shop/paymentAgreement/delete/`,
    changeState: `${server}/api/v2/shop/paymentAgreement/changeState/`,
    getQuote: `${server}/api/v2/shop/paymentAgreement/getQuote/`,
  },
  extraIncome: {
    list: `${server}/api/v2/shop/extraIncome/list/`,
    add: `${server}/api/v2/shop/extraIncome/add/`,
    delete: `${server}/api/v2/shop/extraIncome/delete/`,
    addCategory: `${server}/api/v2/shop/extraIncome/addCategory/`,
  },
  adjustments: {
    stats: `${server}/api/v2/shop/accountingAdjustments/stats/`,
    
  },
  product: {
    main: `${server}/api/v2/shop/product/`,
    search_products: `${server}/api/v2/shop/product/search/`,
  },
  taxesFiscalYear: {
    list: `${server}/api/v2/shop/taxesFiscalYear/list/`,
  },
  externalServices: {
    list: `${server}/api/v2/shop/externalServices/list/`,
    add: `${server}/api/v2/shop/externalServices/add/`,
    delete: `${server}/api/v2/shop/externalServices/delete/`,
  },
  fundGroups: `${server}/api/v2/shop/fundGroups/`,
};

interface IIncomeReportData {
  month: string;
  related_to: string;
  fiscalYearId: number;
  download: boolean | undefined;
  date: string | undefined;
  currency?: string;
  conversionRate?: number;
}
const useShop = () => {
  const { currentCustomer } = useSelector(
    (state: { customers: any }) => state.customers
  );
  const { t } = useTranslation();
  const { api } = useAsyncRequest();
  const dispatch = useDispatch();

  const getTaxesFiscalYears = async () => {
    var res = await api(shopUrls.taxesFiscalYear.list, {
      customer_id: currentCustomer.id,
    });
    dispatch(setShopState({ taxesFiscalYears: res.data.data }));
  };

  const searchUserAccounts = async (term: string) => {
    var res = await api(shopUrls.userAccount.search, {
      customer_id: currentCustomer.id,
      search_text: term,
    });
    return res;
  };

  const getIncomeReportData = async (data: IIncomeReportData) => {
    var res = await api(
      shopUrls.addFund.incomeReport,
      {
        customer_id: currentCustomer.id,
        month: data.month,
        related_to: data.related_to,
        fiscal_year_id: data.fiscalYearId,
        download: data.download,
        date: data.date || "bank",
        currency: data.currency || "current",
        conversion_rate: data.conversionRate || 1,
      },
      {},
      null
    );
    return res;
  };

  const getIncomeReportList = async () => {
    var res = await api(shopUrls.addFund.incomeReportList, {
      customer_id: currentCustomer.id,
    });
    return res;
  };

  const getIncomeReportDetail = async (id: number) => {
    var res = await api(shopUrls.addFund.incomeReportDetail, {
      customer_id: currentCustomer.id,
      id: id,
    });
    return res;
  };

  const getIncomeReportPdf = async (id: number, xls: boolean | undefined) => {
    var res = await api(
      shopUrls.addFund.incomeReportPdf,
      {
        customer_id: currentCustomer.id,
        id: id,
        xls: xls,
      },
      {
        responseType: "blob",
      },
      t("Generando reporte de ingresos")
    );
    if (res.status === 200) {
      handleDownloadFile(res);
    } else {
      console.log(res);
    }
  };

  const checkExternalServices = async (data) => {
    try {
      var res = await api(shopUrls.userAccount.checkExternalServices, {
        customer_id: currentCustomer.id,
        ...data,
      });
      return res;
    } catch (error) {
      return error;
    }
  };

  const approveFromExcel = async (data: any) => {
    try {
      var f = new FormData();
      Object.keys(data).forEach((key) => {
        f.append(key, data[key]);
      });
      f.append("customer_id", currentCustomer.id);

      var res = await api(
        shopUrls.addFund.approveFromExcel,
        f,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
        t("Aprobando desde Excel")
      );

      return res;
    } catch (error) {
      console.log(error);
      return error;
    }
  };

  // Payment agreement
  const createPaymentAgreement = async (data: any) => {
    try {
      var res = await api(shopUrls.paymentAgreement.create, {
        customer_id: currentCustomer.id,
        ...data,
      });
      return res;
    } catch (error) {
      return error;
    }
  };

  const updatePaymentAgreement = async (data: any) => {
    try {
      var res = await api(shopUrls.paymentAgreement.update, {
        customer_id: currentCustomer.id,
        ...data,
      });
      return res;
    } catch (error) {
      return error;
    }
  };

  const getPaymentAgreementDetail = async (id: number) => {
    try {
      var res = await api(shopUrls.paymentAgreement.detail, {
        customer_id: currentCustomer.id,
        id: id,
      });
      return res;
    } catch (error) {
      return error;
    }
  };

  const getPaymentAgreementQuote = async (data: any) => {
    try {
      var res = await api(shopUrls.paymentAgreement.getQuote, {
        customer_id: currentCustomer.id,
        ...data,
      });
      return res;
    } catch (error) {
      return error;
    }
  };

  const getPaymentAgreementList = async () => {
    try {
      var res = await api(shopUrls.paymentAgreement.list, {
        customer_id: currentCustomer.id,
      });
      return res;
    } catch (error) {
      return error;
    }
  };

  const deletePaymentAgreement = async (id: number) => {
    try {
      var res = await api(shopUrls.paymentAgreement.delete, {
        customer_id: currentCustomer.id,
        id: id,
      });
      return res;
    } catch (error) {
      return error;
    }
  };

  const changePaymentAgreementState = async (id: number, state: string) => {
    try {
      var res = await api(shopUrls.paymentAgreement.changeState, {
        customer_id: currentCustomer.id,
        id: id,
        state: state,
      });
      return res;
    } catch (error) {
      return error;
    }
  };

  const getFundGroups = async () => {
    try {
      var res = await api(shopUrls.fundGroups, {
        customer_id: currentCustomer.id,
      });
      dispatch(setShopState({ fundGroups: res.data.data }));
      return res;
    } catch (error) {
      return error;
    }
  };

  useEffect(() => {
    return () => {};
  }, []);

  useEffect(() => {
    getTaxesFiscalYears();
  }, [currentCustomer]);

  return {
    getIncomeReportData,
    getIncomeReportList,
    getIncomeReportDetail,
    getIncomeReportPdf,
    checkExternalServices,
    searchUserAccounts,
    addFund: {
      approveFromExcel,
    },
    paymentAgreement: {
      createPaymentAgreement,
      updatePaymentAgreement,
      getPaymentAgreementDetail,
      getPaymentAgreementList,
      deletePaymentAgreement,
      changePaymentAgreementState,
      getPaymentAgreementQuote,
    },
    getFundGroups,
  };
};

export default useShop;
